import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {toast} from 'react-toastify';
import {Spinner} from '../Spinner';
import {isOk} from '../../utils/apiResponse';
import {Ga4AttributionSection} from '../client/ad_accounts/Ga4AttributionSection';
import {isForbidden, saveAccountRule} from './ga4AttributionApi';
import '../../sass/elements/_ga4-attribution.scss';

/**
 * La regla de cruce de UNA cuenta, sola en un modal: lo abren la tarjeta de la agencia («Cuentas con su
 * propia regla») y la línea («Cambiar»), que no tienen el modal del límite diario a mano.
 *
 * Es la misma `Ga4AttributionSection` del modal de la cuenta, con su propio Guardar: sin cambios no hay
 * nada que guardar y el botón queda apagado. Al guardar avisa `onSaved(payload)` con lo que respondió el
 * PUT (el mismo shape del GET de la cuenta), para que quien lo abrió se actualice sin volver a pedir.
 *
 * La sección se monta sólo con el modal abierto: así pide la regla al abrirse, y cada apertura arranca
 * de lo guardado.
 */
export function Ga4AttributionModal({
    isOpen, onClose, clientAdAccountId, channel, accountName, onSaved, seeRuleLink = true,
}) {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) setDraft(null);
    }, [isOpen]);

    const close = () => {
        if (!saving) onClose();
    };

    const save = () => {
        if (!draft || saving) return;
        setSaving(true);
        saveAccountRule(clientAdAccountId, draft).then((response) => {
            setSaving(false);
            if (isForbidden(response)) {
                toast.error(t('ga4_attribution.agency.forbidden'));
                return;
            }
            if (!isOk(response)) {
                toast.error(t('ga4_attribution.agency.save_error'));
                return;
            }
            toast.success(t('ga4_attribution.agency.saved_ok'));
            if (onSaved) onSaved(response.data);
            onClose();
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={close} className="ga4-attr-modal">
            <ModalHeader toggle={close}>{accountName || t('ga4_attribution.account.title')}</ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    {isOpen && clientAdAccountId && (
                        <Ga4AttributionSection clientAdAccountId={clientAdAccountId} channel={channel}
                                               onDraftChange={setDraft} seeRuleLink={seeRuleLink} disabled={saving}/>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </button>
                <button type="button" className="btn -alt ga4-attr-modal__save" onClick={save}
                        disabled={!draft || saving} aria-busy={saving}>
                    {saving && <Spinner color="negative very-small"/>}
                    {t(saving ? 'ga4_attribution.agency.saving' : 'ga4_attribution.agency.save')}
                </button>
            </ModalFooter>
        </Modal>
    );
}

export default Ga4AttributionModal;
